import { usePurchaseContext } from '~/purchase/context';
import type { IOrder } from '@wyz/types';
import {
  Badge,
  Button,
  CenteredSpinner,
  Flex,
  FormatDate,
  FormContainer,
  Icon,
  notify,
  Text,
  TextField,
  Title,
} from '@wyz/ui';
import {
  DateUtils,
  invariant,
  OFFER_TYPE,
  ORDER_TYPE_CODE,
  useModalState,
  useOnSuccessQuery,
} from '@wyz/utils';
import { sumBy } from 'lodash-es';
import * as React from 'react';
import { Card, Col, Row } from '@wyz/ui';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useConfig, useLocale, useI18n, Trans } from '~/common/context';
import { PurchaseErrorCodes } from '~/common/helpers';
import { useManageRedistributionOrders } from '~/purchase/api';
import {
  CancelPendingOrderModal,
  ConfirmOrderModal,
  PendingOrderTotalSection,
  PendingOrderTyreItem,
  SuggestedTyreTable,
} from '~/purchase/components';
import {
  RedistributionOrderHelpers,
  RedistributionPricingHelpers,
} from '~/purchase/helpers';
import { ACCEPT_ORDER_ACTIONS } from '~/purchase/schemas';
import { IAcceptOrderPayload } from '~/purchase/types';

type Props = {
  order: IOrder;
};

const AcceptPendingView: React.FC<Props> = ({ order }) => {
  const { parentStockOrder } = order;
  const { t } = useI18n();
  const navigate = useNavigate();
  const { state } = usePurchaseContext();
  const locale = useLocale();

  invariant(state.targetCustomer);
  const { tenantCode } = useConfig();
  const manageRedistributionOrders = useManageRedistributionOrders({
    accept: { enabled: true },
    getSuggestedOffers: {
      enabled: true,
      args: {
        reference: order.orderReference,
        parentCustomerId: state.targetCustomer.id,
      },
    },
  });

  const [cancelOrderModal, cancelOrderModalHandlers] = useModalState<
    'CANCEL_ORDER',
    { orderReference: string }
  >({ defaultValue: undefined });

  const [confirmOrderModal, confirmOrderModalHandlers] =
    useModalState<'CONFIRM_ORDER'>({ defaultValue: undefined });

  const isDestockingOrder = order.type === ORDER_TYPE_CODE.DESTOCKING;

  const methods = useForm<IAcceptOrderPayload>({
    reValidateMode: 'onChange',
    defaultValues: {
      action: ACCEPT_ORDER_ACTIONS.ACCEPT_ORDER,
      supplierComment: '',
      comment: order.comment ?? '',
      items: [],
    },
  });

  const watchedItems = useWatch({
    control: methods.control,
    name: 'items',
  });

  // For parent stock orders, treat any quantity change as empty stock
  const hasQuantityChangeForParentStock =
    parentStockOrder &&
    manageRedistributionOrders.accept.isError &&
    manageRedistributionOrders.accept.error.errors?.some(
      (error) => error.quantityChanged,
    );

  const isEmptyStock =
    (manageRedistributionOrders.accept.isError &&
      manageRedistributionOrders.accept.error.errors?.length &&
      manageRedistributionOrders.accept.error.errors.every(
        (error) => error.actual.availableQuantity === 0,
      )) ||
    hasQuantityChangeForParentStock;

  useOnSuccessQuery(
    manageRedistributionOrders.getSuggestedOffers,
    (response) => {
      methods.setValue(
        'items',
        response.data.map((suggestedOffer) => ({
          lineId: suggestedOffer.offerId,
          supplierId: suggestedOffer.offer.supplier.id,
          ean: order.tyreLines.at(0)?.ean ?? '',
          quantity: suggestedOffer.offer.proposedQuantity ?? 0,
          deliveryDate: suggestedOffer.offer.deliveryDate,
          type: suggestedOffer.offer.offerType,
          sellingPrice: suggestedOffer.offer.redistributionSellingPrice,
          warehouseId: suggestedOffer.offer.warehouseId,
        })),
      );
    },
  );

  const totalRequestedQuantity = sumBy(
    order.tyreLines,
    (item) => item.quantity,
  );

  const totalLocalStock = manageRedistributionOrders.getSuggestedOffers
    .isSuccess
    ? sumBy(
        manageRedistributionOrders.getSuggestedOffers.data.data.filter(
          (item) => item.offer.offerType === OFFER_TYPE.DISTRIBUTOR_PLATFORM,
        ),
        (item) => item.offer.availableQuantity,
      )
    : 0;

  const isFullyCoveredByLocalStock =
    totalRequestedQuantity <= totalLocalStock ||
    Boolean(
      manageRedistributionOrders.getSuggestedOffers.data?.data.every(
        (item) => item.offer.offerType === OFFER_TYPE.DISTRIBUTOR_PLATFORM,
      ),
    );
  const totalValues = RedistributionPricingHelpers.calculatePricing(
    manageRedistributionOrders.getSuggestedOffers.data?.data ?? [],
    watchedItems,
  );

  const totalQuantity = React.useMemo(() => {
    return sumBy(watchedItems, (item) => item.quantity);
  }, [watchedItems]);

  const onSubmit = (data: IAcceptOrderPayload) => {
    invariant(state.targetCustomer);
    manageRedistributionOrders.accept.mutate(
      {
        ...data,
        orderReference: order.orderReference,
        parentCustomerId: state.targetCustomer.id,
        childCustomerId: order.customerPublicKey,
      },
      {
        onSuccess: () => {
          notify.success(
            <Trans
              i18nKey='app.purchase.redistribution.accept.success'
              values={{
                reference: order.orderReference,
              }}
            />,
            { id: 'toast-success-pending-order-accepted' },
          );
          void navigate('..');
        },
        onError: (error) => {
          if (
            error.message === PurchaseErrorCodes.ERR_ACCEPT_ORDER_DATA_CHANGED
          ) {
            const previousItems = methods.getValues('items');
            const newItems = RedistributionOrderHelpers.applyOfferChanges(
              error,
              previousItems,
            );
            methods.setValue('items', newItems);
            notify.error(
              <Trans i18nKey='app.purchase.redistribution.order.submit_changes_message' />,
              { id: 'toast-error-pending-order-data-changed' },
            );
          } else {
            notify.error(<Trans i18nKey='app.common.messages.error' />, {
              id: 'toast-error-pending-order-accept-failed',
            });
          }
        },
      },
    );
  };

  const handleOnPreSubmit = (
    action: IAcceptOrderPayload['action'],
    formData: IAcceptOrderPayload,
  ) => {
    if (totalQuantity === 0) {
      notify.error(
        <Trans i18nKey='app.purchase.redistribution.order_accept.errors.empty_quantity' />,
        { id: 'toast-error-pending-order-empty-quantity' },
      );
      return;
    }
    if (totalQuantity > totalRequestedQuantity) {
      notify.error(
        <Trans i18nKey='app.purchase.redistribution.order_accept.errors.gt_requested' />,
        { id: 'toast-error-pending-order-quantity-exceeds-requested' },
      );
      return;
    }
    if (
      action === ACCEPT_ORDER_ACTIONS.ACCEPT_ORDER &&
      (isDestockingOrder || isFullyCoveredByLocalStock === false)
    ) {
      confirmOrderModalHandlers.open('CONFIRM_ORDER');
      return;
    }
    onSubmit({ ...formData, action });
  };

  return (
    <>
      <FormContainer methods={methods} submitHandler={(data) => onSubmit(data)}>
        <Row>
          <Col sm={12} className='mt-3'>
            <Title heading='lg' weight='bold'>
              <Trans
                i18nKey='app.purchase.redistribution.order_accept.title'
                values={{
                  reference: order.orderReference,
                  customerName: order.customerName,
                }}
              />
            </Title>
          </Col>
          <Col sm={12} lg={8} className='mt-3'>
            <Card className='mt-2 h-100'>
              <Card.Header className='bg-neutral-grey-1 p-4 rounded-top border-bottom border-neutral-gray-4'>
                <Flex alignItems='center' className='space-x-2'>
                  <Title weight='bold'>
                    <Trans i18nKey='app.purchase.redistribution.customer_order.title' />
                  </Title>
                  <Text color='secondary-gray' heading='sm' weight='normal'>
                    <FormatDate
                      date={order.orderDatetimeUtc}
                      format={DateUtils.DATE_FORMAT.DMY_HM}
                      timezone={
                        state.primaryCustomer?.deliveryInformation.timeZone
                      }
                      locale={locale.locale}
                    />
                  </Text>
                  <div className='ms-auto'>
                    <Text
                      className='d-inline'
                      weight='semi-bold'
                      color='secondary-gray'
                    >
                      <Trans i18nKey='app.purchase.orders.accept.user' />
                    </Text>
                    <Text
                      className='d-inline'
                      color='secondary'
                      weight='semi-bold'
                    >
                      {order.ordererName}
                    </Text>
                  </div>
                </Flex>
                {order.tyreLines.map((orderItem) => {
                  return (
                    <div className='mt-2' key={orderItem.ean}>
                      {state.targetCustomer && (
                        <PendingOrderTyreItem
                          orderItem={orderItem}
                          supplier={order.supplierName}
                          deliveryDate={order.dateDelivered}
                          orderType={order.type}
                          customer={state.targetCustomer}
                        />
                      )}
                    </div>
                  );
                })}
              </Card.Header>
              <Card.Body>
                <Flex alignItems='center' className='space-x-2'>
                  <Title weight='bold'>
                    <Trans i18nKey='app.purchase.redistribution.order_accept.refresh' />{' '}
                  </Title>{' '}
                  <Text color='secondary-gray' heading='sm' weight='normal'>
                    <FormatDate
                      date={new Date()}
                      format={DateUtils.DATE_FORMAT.DMY_HM}
                      locale={locale.locale}
                    />
                  </Text>
                </Flex>
                {!manageRedistributionOrders.getSuggestedOffers.isSettled && (
                  <CenteredSpinner />
                )}

                {manageRedistributionOrders.getSuggestedOffers.isSuccess && (
                  <SuggestedTyreTable
                    offers={
                      manageRedistributionOrders.getSuggestedOffers.data.data
                    }
                    changes={
                      manageRedistributionOrders.accept.error?.errors ?? []
                    }
                    isLoading={manageRedistributionOrders.accept.isLoading}
                    order={order}
                    isFullyCoveredByLocalStock={isFullyCoveredByLocalStock}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col sm={12} lg={4} className='mt-3'>
            {order.comment && (
              <Card className='mt-2'>
                <Card.Body className='bg-neutral-grey-1'>
                  <Flex>
                    <Icon
                      icon='chat-line'
                      size='xs'
                      className='text-royal-blue'
                    />
                    <Title heading='lg' weight='bold' className='mb-3' wrap>
                      <Trans i18nKey='app.common.comment_of_customer' />
                    </Title>
                  </Flex>
                  {order.comment}
                </Card.Body>
              </Card>
            )}
            <Card className='mt-2 h-100'>
              <Card.Body className='bg-neutral-grey-1'>
                <Flex>
                  <span className='text-royal-blue'>
                    <Icon icon='chat-line' size='xs' />
                  </span>
                  <Title heading='lg' weight='bold' className='mb-3' wrap>
                    <Trans i18nKey='app.purchase.redistribution.order_accept.comment' />
                  </Title>
                </Flex>
                <TextField
                  rows={12}
                  type='text'
                  name='supplierComment'
                  as='textarea'
                  translate={t}
                  placeholder={t(
                    'app.purchase.redistribution.order_accept.comment_placeholder',
                  )}
                  disabled={
                    manageRedistributionOrders.getSuggestedOffers.isError ||
                    !manageRedistributionOrders.getSuggestedOffers.isSettled
                  }
                />
              </Card.Body>
            </Card>
          </Col>
          {manageRedistributionOrders.getSuggestedOffers.isSuccess && (
            <>
              <Col sm={12} lg={8} className='mt-3'>
                {manageRedistributionOrders.getSuggestedOffers.data.data
                  .length > 0 &&
                  !isEmptyStock && (
                    <PendingOrderTotalSection
                      values={totalValues}
                      currency={order.currency}
                    />
                  )}
              </Col>
              <Col sm={12} lg={4} className='mt-3'>
                <Card>
                  <Card.Body className='bg-neutral-grey-1'>
                    <Flex
                      className='mb-2'
                      alignContent='center'
                      justifyContent='between'
                    >
                      <Text
                        weight='semi-bold'
                        color='secondary-gray'
                        heading='sm'
                        className='pt-2'
                      >
                        <Trans i18nKey='app.purchase.redistribution.order_accept.selected_quantity' />
                      </Text>
                      {totalQuantity >= totalRequestedQuantity ? (
                        <Text
                          color='black'
                          weight='bold'
                          heading='sm'
                          className='pt-2'
                        >
                          {totalQuantity}
                        </Text>
                      ) : (
                        <Badge bg='warning' className='text-nowrap'>
                          <Flex direction='row'>
                            <Icon icon='alert' />
                            <Text
                              heading='sm'
                              weight='bold'
                              color='warning'
                              className='pt-2'
                            >
                              {totalQuantity}
                            </Text>
                          </Flex>
                        </Badge>
                      )}
                    </Flex>
                    <Flex
                      className='mb-4'
                      alignContent='center'
                      justifyContent='between'
                    >
                      <Text
                        weight='semi-bold'
                        color='secondary-gray'
                        heading='sm'
                      >
                        <Trans i18nKey='app.purchase.redistribution.order_accept.total_buying_price_excl_tax' />
                      </Text>
                      <Text weight='bold' color='black' heading='sm'>
                        {locale.formatters.money(
                          totalValues.sellingTotalExclTax,
                          {
                            currency: order.currency,
                          },
                        )}
                      </Text>
                    </Flex>
                    <Flex
                      alignContent='center'
                      className='space-y-4'
                      direction='column'
                    >
                      {watchedItems.length !== 0 && !isEmptyStock && (
                        <Button
                          id='accept-order'
                          variant='primary'
                          isLoading={
                            manageRedistributionOrders.accept.isLoading
                          }
                          disabled={manageRedistributionOrders.accept.isLoading}
                          onClick={methods.handleSubmit((data) => {
                            return handleOnPreSubmit(
                              ACCEPT_ORDER_ACTIONS.ACCEPT_ORDER,
                              data,
                            );
                          })}
                        >
                          <Trans i18nKey='app.purchase.redistribution.order_accept.accept_btn' />
                        </Button>
                      )}
                      {watchedItems.length !== 0 &&
                        !isEmptyStock &&
                        !isFullyCoveredByLocalStock &&
                        manageRedistributionOrders.getSuggestedOffers
                          .isSuccess && (
                          <Button
                            id='accept-without-order'
                            variant='secondary'
                            isLoading={
                              manageRedistributionOrders.accept.isLoading
                            }
                            disabled={
                              manageRedistributionOrders.accept.isLoading
                            }
                            onClick={methods.handleSubmit((data) => {
                              return handleOnPreSubmit(
                                ACCEPT_ORDER_ACTIONS.ACCEPT_WITHOUT_ORDER,
                                data,
                              );
                            })}
                          >
                            <Trans i18nKey='app.purchase.redistribution.order_accept.accept_without_order_btn' />
                          </Button>
                        )}
                      <Button
                        onClick={() => {
                          invariant(state.targetCustomer);
                          cancelOrderModalHandlers.open('CANCEL_ORDER', {
                            orderReference: order.orderReference,
                          });
                        }}
                        id='refuse-order'
                        variant='danger'
                        disabled={
                          !manageRedistributionOrders.getSuggestedOffers
                            .isSuccess ||
                          manageRedistributionOrders.accept.isLoading
                        }
                      >
                        <Trans i18nKey='app.common.button.order_cancel' />
                      </Button>
                    </Flex>
                  </Card.Body>
                </Card>
              </Col>
            </>
          )}
          {manageRedistributionOrders.getSuggestedOffers.isSuccess &&
            confirmOrderModal === 'CONFIRM_ORDER' && (
              <ConfirmOrderModal
                variant={isDestockingOrder ? 'DESTOCKING' : 'ORDERABLE'}
                onClose={confirmOrderModalHandlers.close}
                offers={manageRedistributionOrders.getSuggestedOffers.data.data}
                watchedItems={watchedItems}
                onConfirm={methods.handleSubmit((data) => {
                  confirmOrderModalHandlers.close();
                  onSubmit({
                    ...data,
                    action: ACCEPT_ORDER_ACTIONS.ACCEPT_ORDER,
                  });
                })}
              />
            )}
        </Row>
      </FormContainer>
      {manageRedistributionOrders.getSuggestedOffers.isSuccess &&
        cancelOrderModal === 'CANCEL_ORDER' &&
        state.targetCustomer && (
          <CancelPendingOrderModal
            customerId={state.targetCustomer.id}
            tenantCode={tenantCode}
            orderReference={order.orderReference}
            onClose={cancelOrderModalHandlers.close}
            onSuccess={() => {
              void navigate('..');
            }}
            initialComment={methods.getValues('supplierComment')}
          />
        )}
    </>
  );
};

export default AcceptPendingView;
